import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, PlusIcon, ArrowPathIcon, PencilSquareIcon, CheckIcon } from '@heroicons/react/24/outline';
import { AIShortcut } from '@/hooks/useAIShortcuts';
import { toast } from 'react-hot-toast';
import { Input } from '@/components/forms';

interface AIShortcutsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    shortcuts: AIShortcut[];
    addShortcut: (shortcut: Omit<AIShortcut, 'id'>) => Promise<void>;
    updateShortcut: (shortcut: AIShortcut) => Promise<void>;
    deleteShortcut: (id: string) => Promise<void>;
    reorderShortcuts: (shortcuts: AIShortcut[]) => Promise<void>;
    resetToDefaults: () => Promise<void>;
}

// Reuse input classes for textarea since it doesn't have a dedicated component
const inputClasses = 'block w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm';

// Sub-component for individual shortcut item
const ShortcutItem: React.FC<{
    shortcut: AIShortcut;
    index: number;
    total: number;
    isEditing: boolean;
    onEdit: () => void;
    onSave: (name: string, prompt: string) => Promise<void>;
    onCancel: () => void;
    onDelete: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}> = ({
    shortcut,
    index,
    total,
    isEditing,
    onEdit,
    onSave,
    onCancel,
    onDelete,
    onMoveUp,
    onMoveDown
}) => {
        const [name, setName] = useState(shortcut.name);
        const [prompt, setPrompt] = useState(shortcut.prompt);
        const [isSaving, setIsSaving] = useState(false);

        // Reset local state when editing mode changes or shortcut prop changes
        useEffect(() => {
            if (isEditing) {
                setName(shortcut.name);
                setPrompt(shortcut.prompt);
            }
        }, [isEditing, shortcut]);

        const handleSave = async () => {
            if (!name.trim() || !prompt.trim()) {
                toast.error('Name and Prompt are required');
                return;
            }
            setIsSaving(true);
            try {
                await onSave(name, prompt);
            } finally {
                setIsSaving(false);
            }
        };

        if (isEditing) {
            return (
                <div className="p-4 bg-blue-50/50 border border-blue-200 rounded-lg mb-3 flex flex-col gap-3 shadow-sm">
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Display Name</label>
                        <Input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Refine Text"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">AI Prompt</label>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={2}
                            className={inputClasses}
                            placeholder="e.g. Fix spelling and grammar..."
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            onClick={onCancel}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200/50 rounded-md transition-colors"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-3 py-1.5 flex items-center gap-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-colors"
                        >
                            {isSaving ? 'Saving...' : (
                                <>
                                    <CheckIcon className="w-3.5 h-3.5" />
                                    Save
                                </>
                            )}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className={`group flex items-start gap-3 p-3 bg-white border rounded-lg mb-2 transition-all hover:border-gray-300 hover:shadow-sm ${index < 5 ? 'border-l-4 border-l-blue-500' : 'border-gray-200'}`}>
                <div className="flex flex-col gap-1 mt-0.5">
                    <button
                        onClick={onMoveUp}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-900 disabled:opacity-20 disabled:cursor-not-allowed p-0.5 rounded hover:bg-gray-100 transition-colors"
                        title="Move Up"
                    >
                        <ArrowUpIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={onMoveDown}
                        disabled={index === total - 1}
                        className="text-gray-400 hover:text-gray-900 disabled:opacity-20 disabled:cursor-not-allowed p-0.5 rounded hover:bg-gray-100 transition-colors"
                        title="Move Down"
                    >
                        <ArrowDownIcon className="w-3.5 h-3.5" />
                    </button>
                </div>

                <div className="flex-grow min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-sm text-gray-900 truncate">{shortcut.name}</h4>
                        {index < 5 && (
                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold border border-blue-100 whitespace-nowrap">
                                Active
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed" title={shortcut.prompt}>
                        {shortcut.prompt}
                    </p>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={onEdit}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                        title="Edit"
                    >
                        <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => {
                            if (confirm('Delete this shortcut?')) {
                                onDelete();
                            }
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                        title="Delete"
                    >
                        <TrashIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

export default function AIShortcutsDialog({
    isOpen,
    onClose,
    shortcuts,
    addShortcut,
    updateShortcut,
    deleteShortcut,
    reorderShortcuts,
    resetToDefaults
}: AIShortcutsDialogProps) {
    // ID of the shortcut currently being edited.
    // special 'NEW_TEMP_ID' means adding a new one.
    const [editingId, setEditingId] = useState<string | null>(null);

    const handleAddNew = () => {
        setEditingId('NEW_TEMP_ID');
    };

    const handleMove = async (index: number, direction: 'up' | 'down') => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === shortcuts.length - 1)
        ) {
            return;
        }

        const newShortcuts = [...shortcuts];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap
        [newShortcuts[index], newShortcuts[targetIndex]] = [newShortcuts[targetIndex], newShortcuts[index]];

        await reorderShortcuts(newShortcuts);
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-xl bg-white text-left align-middle shadow-2xl transition-all flex flex-col max-h-[85vh]">
                                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                                        AI Shortcuts
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="rounded-full p-1.5 hover:bg-gray-200 text-gray-500 transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                                    <div className="mb-6 flex flex-col gap-4">
                                        <div className="flex justify-between items-start gap-4">
                                            <p className="text-sm text-gray-500 leading-relaxed">
                                                Customize your AI commands. The top 5 shortcuts will be pinned to your quick actions bar.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAddNew}
                                            disabled={!!editingId}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-dashed border-gray-300 text-gray-600 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                        >
                                            <PlusIcon className="w-5 h-5" />
                                            <span>Add New Shortcut</span>
                                        </button>
                                    </div>

                                    {/* Create New Item Editor */}
                                    {editingId === 'NEW_TEMP_ID' && (
                                        <div className="animate-in fade-in slide-in-from-top-4 duration-200 mb-4">
                                            <ShortcutItem
                                                shortcut={{ id: 'NEW_TEMP_ID', name: '', prompt: '' }}
                                                index={-1}
                                                total={shortcuts.length + 1}
                                                isEditing={true}
                                                onEdit={() => { }}
                                                onSave={async (name, prompt) => {
                                                    await addShortcut({ name, prompt });
                                                    setEditingId(null);
                                                }}
                                                onCancel={() => setEditingId(null)}
                                                onDelete={() => setEditingId(null)}
                                                onMoveUp={() => { }}
                                                onMoveDown={() => { }}
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        {shortcuts.map((shortcut, index) => (
                                            <ShortcutItem
                                                key={shortcut.id}
                                                shortcut={shortcut}
                                                index={index}
                                                total={shortcuts.length}
                                                isEditing={editingId === shortcut.id}
                                                onEdit={() => setEditingId(shortcut.id)}
                                                onSave={async (name, prompt) => {
                                                    await updateShortcut({ ...shortcut, name, prompt });
                                                    setEditingId(null);
                                                }}
                                                onCancel={() => setEditingId(null)}
                                                onDelete={async () => {
                                                    if (editingId === shortcut.id) setEditingId(null);
                                                    await deleteShortcut(shortcut.id);
                                                }}
                                                onMoveUp={() => handleMove(index, 'up')}
                                                onMoveDown={() => handleMove(index, 'down')}
                                            />
                                        ))}

                                        {shortcuts.length === 0 && editingId !== 'NEW_TEMP_ID' && (
                                            <div className="text-center py-12 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-100">
                                                <p className="text-gray-400 text-sm">No shortcuts defined yet.</p>
                                                <p className="text-gray-400 text-xs mt-1">Click "Add New Shortcut" to get started.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="px-6 py-4 bg-gray-50 flex justify-between items-center border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (confirm('This will replace your current shortcuts with the defaults. Continue?')) {
                                                resetToDefaults();
                                            }
                                        }}
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 px-3 py-2 hover:bg-gray-200/50 rounded-md transition-colors"
                                    >
                                        <ArrowPathIcon className="w-3.5 h-3.5" />
                                        Reset Defaults
                                    </button>

                                    <button
                                        type="button"
                                        className="px-5 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 shadow-sm transition-all"
                                        onClick={onClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
